import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Divider,
  Stack,
  Box,
  InputAdornment,
  Chip,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewShow,
  viewShowById,
  updateShowById,
} from '../../services/content.service';
import Status from '../form-elements/status.component';
import {
  CONTENT_ALIAS,
  DEFAULT_CHANNEL_VALUE,
} from '../../config/const.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditShows = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      year: null,
      rating: null,
      ageRestrict: null,
      tags: [],
      status: null,
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [tag, setTag] = useState('');
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const tags = watch('tags', []);

  const handleAddTag = () => {
    if (tag && !tags.includes(tag)) {
      const updatedTags = [...tags, tag];
      setValue('tags', updatedTags);
      setTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const updatedTags = tags.filter((t) => t !== tagToRemove);
    setValue('tags', updatedTags);
  };

  const saveNewData = (payload) => {
    createNewShow(payload)
      .then(() => {
        onSuccess(`New ${CONTENT_ALIAS.SHOW_SINGULAR} added successfully.`);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateShowById(dataId, payload)
      .then(() => {
        onSuccess(
          `${CONTENT_ALIAS.SHOW_SINGULAR} details updated successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      name: data.name,
      description: data.description,
      year: data.year && data.year !== '' ? Number(data.year) : null,
      rating: data.rating && data.rating !== '' ? Number(data.rating) : null,
      ageRestrict:
        data.ageRestrict && data.ageRestrict !== ''
          ? Number(data.ageRestrict)
          : null,
      tags: data.tags.join(',') || '',
      thumbnail: data.thumbnail || '',
      status: data.status,
      channelId: DEFAULT_CHANNEL_VALUE,
    };
    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewShowById(dataId)
      .then((res) => {
        // Fill form values
        setValue('name', res.data?.name || '');
        setValue('description', res.data?.description || '');
        setValue('year', res.data?.year || null);
        setValue('rating', res.data?.rating || null);
        setValue('ageRestrict', res.data?.ageRestrict || null);
        setValue(
          'tags',
          res.data?.tags !== '' && res.data?.tags !== null
            ? res.data?.tags.split(',')
            : []
        );
        setValue('thumbnail', res.data?.thumbnail || '');
        setValue('status', res.data?.status || 0);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      onClose={() => onCancel()}
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...{ ...maxLengthValidation(255) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`${CONTENT_ALIAS.SHOW_SINGULAR} Name`}
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...{ ...maxLengthValidation(1500) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      type="text"
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                      multiline
                      rows={3}
                      inputProps={{ maxLength: 1500 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="year"
                  control={control}
                  rules={{
                    ...maxLengthValidation(4),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Year"
                      type="text"
                      error={!!errors.year}
                      helperText={errors?.year?.message || null}
                      fullWidth
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 4,
                      }}
                      onInput={(e) => {
                        // Allow only digits and limit to 4
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 4);
                        e.target.value = value;
                        field.onChange(value);
                      }}
                      onPaste={(e) => {
                        e.preventDefault(); // block normal paste
                        const paste = (
                          e.clipboardData || window.clipboardData
                        ).getData('text');
                        const digits = paste.replace(/\D/g, '').slice(0, 4); // keep only first 4 digits
                        field.onChange(digits);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="rating"
                  control={control}
                  rules={{
                    ...maxLengthValidation(4),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rating"
                      type="text"
                      error={!!errors.rating}
                      helperText={errors?.rating?.message || null}
                      fullWidth
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: '[0-9]*[.,]?[0-9]*',
                      }}
                      onInput={(e) => {
                        let { value } = e.target;

                        // Allow only digits and one dot
                        value = value.replace(/[^0-9.]/g, '');

                        // Prevent more than one dot
                        const parts = value.split('.');
                        if (parts.length > 2) value = `${parts[0]}.${parts[1]}`;

                        // Limit to 2 digits after decimal
                        if (parts[1]?.length > 2)
                          value = `${parts[0]}.${parts[1].slice(0, 2)}`;

                        // Prevent entering value > 10
                        if (parseFloat(value) > 10) value = '10';

                        e.target.value = value;
                        field.onChange(value);
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = (
                          e.clipboardData || window.clipboardData
                        ).getData('text');
                        let value = pasted.replace(/[^0-9.]/g, '');
                        const parts = value.split('.');
                        if (parts.length > 2) value = `${parts[0]}.${parts[1]}`;
                        if (parts[1]?.length > 2)
                          value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                        if (parseFloat(value) > 10) value = '10';
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={4}>
                <Controller
                  name="ageRestrict"
                  control={control}
                  rules={{
                    ...maxLengthValidation(3),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Age Restrict"
                      type="text"
                      error={!!errors.ageRestrict}
                      helperText={errors?.ageRestrict?.message || null}
                      fullWidth
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 3,
                      }}
                      onInput={(e) => {
                        // Allow only digits and limit to 3
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 3);
                        e.target.value = value;
                        field.onChange(value);
                      }}
                      onPaste={(e) => {
                        e.preventDefault(); // block normal paste
                        const paste = (
                          e.clipboardData || window.clipboardData
                        ).getData('text');
                        const digits = paste.replace(/\D/g, '').slice(0, 3); // keep only first 3 digits
                        field.onChange(digits);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                size={{
                  xs: 12,
                  sm: 6,
                  md: 6,
                }}
              >
                <Stack direction="column" spacing={1}>
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      label="Add a tag"
                      variant="outlined"
                      value={tag}
                      onChange={(e) => setTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              color="primary"
                              onClick={handleAddTag}
                              sx={{
                                borderRadius: '50%',
                                padding: 1,
                              }}
                            >
                              <Iconify
                                icon="icons8:plus"
                                width={20}
                                height={20}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                {tags.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      minHeight: '40px',
                      maxHeight: '120px',
                      overflow: 'auto',
                    }}
                  >
                    {tags.map((t, index) => (
                      <Chip
                        key={index}
                        label={t}
                        onDelete={() => handleRemoveTag(t)}
                        color="primary"
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                )}

                {/* Hidden field to maintain form state */}
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => <input type="hidden" {...field} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} />
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="status"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Status
                      id="sub_status"
                      name="sub_status"
                      label="Status"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[1, 0]}
                      sx={{ width: '100%' }}
                      error={errors?.status?.message || ''}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="fullWidth"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Image Details (You can upload images from upload section)
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="thumbnail"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                    ...{ ...maxLengthValidation(255) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Thumbnail Url"
                      type="text"
                      error={!!errors.thumbnail}
                      helperText={errors?.thumbnail?.message || null}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditShows.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  categoryTypeName: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditShows;
